import React, { ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Skeleton,
  useMediaQuery,
  Typography,
  Stack,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  TableOptions,
  Row,
  flexRender,
} from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TablePagination from "components/molecules/TablePagination";

export interface ResponsiveTableProps<T extends object> {
  onClickRow?: (row: T) => void;
  pageSize?: number;
  isFirstTimeLoading?: boolean;
  isFetching?: boolean;
  tableOptions?: Pick<TableOptions<T>, "columns" | "data"> &
    Partial<Omit<TableOptions<T>, "columns" | "data">>;
  emptyComponent?: ReactNode;
}

function ResponsiveTable<T extends object>({
  onClickRow,
  pageSize = 10,
  isFirstTimeLoading = false,
  isFetching = false,
  tableOptions,
  emptyComponent,
}: ResponsiveTableProps<T>) {
  // Table instance
  const table = useReactTable<T>({
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize } },
    ...tableOptions,
  });

  const handleRowClick = (row: Row<T>) => {
    onClickRow?.(row.original);
  };

  const isDesktopOrTablet = useMediaQuery((theme) =>
    theme.breakpoints.up("sm")
  );
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const rows = table.getRowModel()?.rows || [];
  const renderMobileTable = (): JSX.Element => {
    return (
      <Box
        sx={{
          outline: "1px solid #e0e0e0",
        }}
        mb={2}
      >
        {isFirstTimeLoading ? (
          Array.from({ length: pageSize }).map((_, i) => (
            <Box
              key={i}
              sx={{
                borderBottom: "1px solid #e0e0e0",
              }}
              p={1}
            >
              <Skeleton variant="rectangular" width="100%" height={20} />
            </Box>
          ))
        ) : rows.length ? (
          rows.map((row, i) => (
            <Stack
              key={row.id}
              onClick={() => handleRowClick(row)}
              style={{ cursor: onClickRow ? "pointer" : undefined }}
              spacing={2}
              p={1}
              sx={{
                backgroundColor: i % 2 === 0 ? "transparent" : "#f8f8f8",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              {row.getVisibleCells().map((cell, i) => {
                const header = table.getHeaderGroups()[0].headers[i];
                return (
                  <Box mb={2}>
                    {(header.column.columnDef.meta as any)
                      ?.hideHeaderOnMobile ? null : (
                      <Typography key={cell.id} fontWeight="bold">
                        <strong>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </strong>
                      </Typography>
                    )}
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Box>
                );
              })}
            </Stack>
          ))
        ) : (
          <Box textAlign="center" p={2}>
            {emptyComponent || <Typography>No data available</Typography>}
          </Box>
        )}
        {!!table.getRowModel().rows?.length && (
          <TablePagination
            pageSize={table.getState().pagination.pageSize}
            totalCount={table.getFilteredRowModel().rows.length}
            pageIndicators={5}
            pageNumber={table.getState().pagination.pageIndex + 1}
            onPageChange={(num: number) => table.setPageIndex(num - 1)}
            loading={isFetching}
          />
        )}
      </Box>
    );
  };

  if (isMobile) {
    return renderMobileTable();
  }

  return (
    <Box role="table">
      <Box
        sx={{
          outline: "1px solid #e0e0e0",
        }}
      >
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: isMobile ? "100%" : 650,
              tableLayout: isMobile ? "auto" : "fixed",
            }}
          >
            <colgroup>
              {tableOptions.columns.map((field, idx) => (
                <col
                  key={idx}
                  style={{
                    width: (field.meta as any)?.width || "auto",
                    minWidth: "100px",
                  }}
                />
              ))}
            </colgroup>
            {isDesktopOrTablet && (
              <TableHead
                sx={{
                  backgroundColor: "#0b3a5d",
                  border: "1px solid 0b3a5d",
                }}
              >
                <TableRow>
                  {table.getHeaderGroups()[0].headers.map((header) => {
                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          color: "white",
                          padding: 1,
                          fontWeight: header.column.getIsSorted()
                            ? "bold"
                            : undefined,
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : "default",
                        }}
                        onClick={
                          header.column.getCanSort() &&
                          header.column.getToggleSortingHandler()
                        }
                      >
                        <Typography
                          fontWeight="bold"
                          component="span"
                          mr={2}
                          sx={{
                            display: "inline-block",
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Typography>
                        {header.column.getCanSort() && (
                          <span>
                            {header.column.getIsSorted() ? (
                              header.column.getIsSorted() === "desc" ? (
                                <FontAwesomeIcon icon="sort-down" />
                              ) : (
                                <FontAwesomeIcon icon="sort-up" />
                              )
                            ) : (
                              <FontAwesomeIcon icon="sort" />
                            )}
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {isFirstTimeLoading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <TableRow
                    sx={{
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    data-loading="true"
                    key={i}
                  >
                    {table.getHeaderGroups()[0].headers.map((header) => (
                      <TableCell key={header.id}>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={20}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row, i) => (
                  <TableRow
                    sx={{
                      backgroundColor: i % 2 === 0 ? "transparent" : "#f8f8f8",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    key={row.id}
                    hover
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: onClickRow ? "pointer" : undefined }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow
                  hover
                  style={{ cursor: onClickRow ? "pointer" : undefined }}
                >
                  <TableCell
                    colSpan={table.getHeaderGroups()[0].headers.length}
                  >
                    {emptyComponent || (
                      <Box textAlign="center" p={2}>
                        <Typography>No data available</Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <TablePagination
        pageSize={table.getState().pagination.pageSize}
        totalCount={table.getFilteredRowModel().rows.length}
        pageNumber={table.getState().pagination.pageIndex + 1}
        onPageChange={(num: number) => table.setPageIndex(num - 1)}
        loading={isFetching}
      />
    </Box>
  );
}

export default ResponsiveTable;
